import {
  ARCADE_CATALOG,
  createArcadeGame,
  getHi,
  type ArcadeGame,
  type ArcadeGameId,
} from "./games";
import { drawArcadePreview } from "./previews";
import { sfx } from "../sfx/SfxEngine";
import type { ResultsOverlay } from "../results/ResultsOverlay";

type CoreLike = {
  onNote: (h: (e: { type: string; midiNote: number; velocity: number; source: string }) => void) => () => void;
  playNoteImmediate: (midi: number, velocity?: number) => void;
  releaseNote?: (midi: number) => void;
  ensureAudio: () => Promise<void>;
  panic?: () => void;
  stopClassSession?: () => void;
  stopExam?: () => void;
  getStatus: () => { classSession: { phase: string }; exam: { phase: string } };
};

/**
 * Bay = cabinet list. Play = dedicated fullscreen overlay (separate from list).
 * Countdown mounts on document.body so parent transforms cannot trap position:fixed.
 */
export class ArcadeHost {
  private grid: HTMLElement;
  private overlay: HTMLElement;
  private canvas: HTMLCanvasElement;
  private titleEl: HTMLElement;
  private scoreEl: HTMLElement;
  private hiEl: HTMLElement;
  private helpEl: HTMLElement;
  private countdownEl: HTMLElement;
  private game: ArcadeGame | null = null;
  private raf = 0;
  private last = 0;
  private ignoreApi = false;
  private playing = false;
  private logicalW = 640;
  private logicalH = 360;
  private unsubNote: (() => void) | null = null;
  private onResizeBound = () => this.resize();
  private countdownTimer: ReturnType<typeof setTimeout> | null = null;
  private activeId: ArcadeGameId | null = null;

  constructor(
    bayRoot: HTMLElement,
    private core: CoreLike,
    private results: ResultsOverlay,
    private ensureSfx: () => Promise<void>,
  ) {
    this.grid = bayRoot.querySelector("#arcade-grid")!;
    this.overlay = document.querySelector("#arcade-play-overlay")!;
    this.canvas = document.querySelector("#arcade-canvas")!;
    this.titleEl = document.querySelector("#arcade-title")!;
    this.scoreEl = document.querySelector("#arcade-score")!;
    this.hiEl = document.querySelector("#arcade-hi")!;
    this.helpEl = document.querySelector("#arcade-help")!;

    // Always attach countdown to body (viewport-centered, above everything)
    let cd = document.querySelector<HTMLElement>("#arcade-countdown");
    if (!cd) {
      cd = document.createElement("div");
      cd.id = "arcade-countdown";
      cd.className = "arcade-countdown";
      cd.hidden = true;
      cd.setAttribute("aria-live", "assertive");
    }
    document.body.appendChild(cd);
    this.countdownEl = cd;

    this.renderCabinets();
    document.querySelector("#btn-arcade-exit")!.addEventListener("click", () => {
      sfx.play("click");
      this.exitGame();
    });
  }

  private renderCabinets(): void {
    this.grid.innerHTML = ARCADE_CATALOG.map(
      (g) => `
      <button type="button" class="arcade-card is-live" data-arcade="${g.id}">
        <canvas class="arcade-preview" data-preview="${g.id}" aria-hidden="true"></canvas>
        <div class="arcade-card-body">
          <h3>${g.title}</h3>
          <p>${g.blurb}</p>
          <span class="arcade-hi-pill">HI ${getHi(g.id)}</span>
        </div>
      </button>`,
    ).join("");
    this.grid.querySelectorAll<HTMLCanvasElement>("[data-preview]").forEach((c) => {
      drawArcadePreview(c, c.dataset.preview as ArcadeGameId);
    });
    this.grid.querySelectorAll<HTMLElement>("[data-arcade]").forEach((btn) => {
      btn.addEventListener("pointerenter", () => sfx.play("hover"));
      btn.addEventListener("click", () => {
        sfx.play("confirm");
        void this.openGame(btn.dataset.arcade as ArcadeGameId);
      });
    });
  }

  refreshHiPills(): void {
    this.grid.querySelectorAll<HTMLElement>("[data-arcade]").forEach((btn) => {
      const id = btn.dataset.arcade as ArcadeGameId;
      const pill = btn.querySelector(".arcade-hi-pill");
      if (pill) pill.textContent = `HI ${getHi(id)}`;
    });
  }

  /** Open dedicated fullscreen game screen, then 3-2-1, then play. */
  async openGame(id: ArcadeGameId): Promise<void> {
    await this.ensureSfx();
    await this.core.ensureAudio();
    const st = this.core.getStatus();
    if (st.classSession.phase === "running") this.core.stopClassSession?.();
    if (st.exam.phase === "running") this.core.stopExam?.();

    this.clearCountdown();
    this.exitGame(false);

    const meta = ARCADE_CATALOG.find((g) => g.id === id)!;
    this.activeId = id;
    this.playing = false;
    this.game = null;

    // Fullscreen play screen — separate from bay list
    this.overlay.hidden = false;
    this.overlay.classList.add("is-open");
    document.body.classList.add("arcade-play-open");
    this.titleEl.textContent = meta.title;
    this.helpEl.textContent = meta.help;
    this.hiEl.textContent = `HI ${getHi(id)}`;
    this.scoreEl.textContent = "Score 0";

    window.scrollTo({ top: 0, behavior: "instant" in window ? ("instant" as ScrollBehavior) : "auto" });
    this.resize();
    this.drawIdleBackdrop(id);
    this.canvas.focus({ preventScroll: true });

    window.addEventListener("resize", this.onResizeBound);
    await this.runCountdown();
    if (this.activeId !== id) return;
    this.beginPlay(id);
  }

  private drawIdleBackdrop(id: ArcadeGameId): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#061018";
    ctx.fillRect(0, 0, this.logicalW, this.logicalH);
    ctx.save();
    ctx.globalAlpha = 0.55;
    const off = document.createElement("canvas");
    drawArcadePreview(off, id);
    ctx.drawImage(off, this.logicalW * 0.15, this.logicalH * 0.18, this.logicalW * 0.7, this.logicalH * 0.5);
    ctx.restore();
    ctx.fillStyle = "rgba(240,193,90,0.95)";
    ctx.font = `700 ${Math.round(this.logicalH * 0.09)}px Oxanium, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("GET READY", this.logicalW / 2, this.logicalH * 0.82);
  }

  private runCountdown(): Promise<void> {
    return new Promise((resolve) => {
      const steps: Array<3 | 2 | 1 | "go"> = [3, 2, 1, "go"];
      let i = 0;
      document.body.appendChild(this.countdownEl);
      this.countdownEl.hidden = false;
      this.countdownEl.classList.add("is-on");

      const tick = () => {
        if (this.activeId == null) {
          this.clearCountdown();
          resolve();
          return;
        }
        const step = steps[i];
        if (step === undefined) {
          this.clearCountdown();
          resolve();
          return;
        }
        this.countdownEl.innerHTML =
          step === "go"
            ? `<span class="cd-num">GO!</span><span class="cd-ring" aria-hidden="true"></span>`
            : `<span class="cd-num">${step}</span><span class="cd-ring" aria-hidden="true"></span>`;
        this.countdownEl.dataset.step = String(step);
        this.countdownEl.classList.remove("pop");
        void this.countdownEl.offsetWidth;
        this.countdownEl.classList.add("pop");
        sfx.speakCount(step);
        i += 1;
        this.countdownTimer = setTimeout(tick, step === "go" ? 780 : 950);
      };
      tick();
    });
  }

  private clearCountdown(): void {
    if (this.countdownTimer != null) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.countdownEl.hidden = true;
    this.countdownEl.classList.remove("is-on", "pop");
    this.countdownEl.textContent = "";
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* ignore */
    }
  }

  private beginPlay(id: ArcadeGameId): void {
    this.game = createArcadeGame(id);
    this.playing = true;
    this.resize();
    this.game.start(
      {
        playTone: (midi, vel = 90) => {
          this.core.playNoteImmediate(midi, vel);
          window.setTimeout(() => this.core.releaseNote?.(midi), 280);
        },
        silence: () => this.core.panic?.(),
        setIgnoreApiNotes: (on) => {
          this.ignoreApi = on;
        },
        setHud: (score, extra) => {
          this.scoreEl.textContent = extra ? `Score ${score} · ${extra}` : `Score ${score}`;
        },
        onGameOver: (score, detail) => {
          this.playing = false;
          this.core.panic?.();
          this.stopLoop();
          this.results.show(
            {
              kind: "arcade",
              title: this.game?.meta.title ?? "ARCADE",
              subtitle: detail,
              score,
              lines: [
                { label: "High score", value: String(getHi(id)) },
                { label: "Mode", value: "ENDLESS" },
              ],
            },
            () => {
              this.exitGame();
              this.refreshHiPills();
            },
          );
        },
      },
      this.logicalW,
      this.logicalH,
    );

    this.unsubNote?.();
    this.unsubNote = this.core.onNote((e) => {
      if (!this.game || !this.playing || e.type !== "noteon") return;
      if (this.ignoreApi && e.source === "api") return;
      this.game.onNote(e.midiNote, e.velocity, e.source);
    });

    this.last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      if (this.game && this.playing) {
        this.game.tick(dt);
        const ctx = this.canvas.getContext("2d");
        if (ctx) {
          const dpr = Math.min(2, window.devicePixelRatio || 1);
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          this.game.draw(ctx);
        }
      }
      this.raf = requestAnimationFrame(loop);
    };
    this.stopLoop();
    this.raf = requestAnimationFrame(loop);
  }

  private stopLoop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  exitGame(showBay = true): void {
    this.clearCountdown();
    this.stopLoop();
    window.removeEventListener("resize", this.onResizeBound);
    this.unsubNote?.();
    this.unsubNote = null;
    this.game?.stop();
    this.game = null;
    this.core.panic?.();
    this.playing = false;
    this.ignoreApi = false;
    this.activeId = null;
    this.overlay.classList.remove("is-open");
    this.overlay.hidden = true;
    document.body.classList.remove("arcade-play-open");
    if (showBay) this.refreshHiPills();
  }

  resize(): void {
    const wrap = this.overlay.querySelector(".arcade-play-inner") as HTMLElement | null;
    const w = Math.max(320, Math.min(960, wrap?.clientWidth ?? window.innerWidth - 32));
    const shortLandscape =
      typeof window !== "undefined" &&
      window.matchMedia("(max-height: 560px) and (orientation: landscape)").matches;
    const heightBudget = shortLandscape ? window.innerHeight * 0.42 : window.innerHeight * 0.55;
    const minH = shortLandscape ? 180 : 300;
    const maxH = shortLandscape ? 320 : 560;
    const h = Math.max(minH, Math.min(maxH, Math.round(Math.min(w * 0.56, heightBudget))));
    this.logicalW = w;
    this.logicalH = h;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.canvas.tabIndex = 0;
    const ctx = this.canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.game?.resize(w, h);
  }

  get active(): boolean {
    return this.activeId != null;
  }
}
